import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NGXLogger } from 'ngx-logger';
import { CookieService } from 'ngx-cookie-service';

export interface QueryParamsBag {
  [key: string]: string | string[];
}

const RESERVED_KEYS = 'variantid,section,error,facets,search'.split(',');

@Injectable({ providedIn: 'root' })
export class QueryParamService {
  private readonly COOKIE_NAME = 'landingQueryParams';
  private readonly EXPIRY_DAYS = 30;
  private params: QueryParamsBag | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private logger: NGXLogger,
    private cookieService: CookieService,
  ) {
    if (isPlatformBrowser(this.platformId)) {
        const stored = this.cookieService.get(this.COOKIE_NAME);
      if (stored) {
        try {
          this.params = JSON.parse(stored);
        } catch (e) {
          this.logger.warn('QueryParamService: failed to parse stored params', e);
          this.params = null;
        }
      }
    }
  }

  /**
   * Capture ALL query parameters from the given URL (or current URL)
   * and persist them for this browser.
   */
  captureFromUrl(url?: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    let href: string;
    if (!url) {
      href = window.location.href;
    } else if (url.startsWith('http://') || url.startsWith('https://')) {
      href = url;
    } else {
      href = window.location.origin + url;
    }

    let u: URL;
    try {
      u = new URL(href);
    } catch (e) {
      this.logger.warn('QueryParamService: invalid URL for capture', href, e);
      return;
    }

    const bag: QueryParamsBag = {};

    u.searchParams.forEach((value, key) => {
        if (RESERVED_KEYS.includes(key.toLowerCase())) {
            return;
        }
        if (bag[key] === undefined) {
            bag[key] = value;
        } else if (Array.isArray(bag[key])) {
            (bag[key] as string[]).push(value);
        } else {
            bag[key] = [bag[key] as string, value];
        }
    });

    if (Object.keys(bag).length === 0) {
      return;
    }

    this.params = bag;
    const isSecure = window.location.protocol === 'https:';
    try {
        this.cookieService.set(
          this.COOKIE_NAME,
          JSON.stringify(bag),
          this.EXPIRY_DAYS,
          '/',
          undefined,
          isSecure,
          'Lax'
        );
      } catch (e) {
        this.logger.warn('QueryParamService: failed to persist params', e);
      }

    this.logger.debug('QueryParamService: captured query params', bag);
  }

  getUtmInfo(): QueryParamsBag | null {
    if (!this.params) {
        return null;
    }
    const utmKeys = ['utm_id', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    const utmInfo: QueryParamsBag = {};
    for (const key of utmKeys) {
        if (this.params[key]) {
            utmInfo[key] = this.params[key];
        }
    }
    return Object.keys(utmInfo).length > 0 ? utmInfo: null;
  }

  getAll(): QueryParamsBag | null {
    return this.params;
  }

  clear(): void {
    this.params = null;
    if (isPlatformBrowser(this.platformId)) {
      this.cookieService.delete(this.COOKIE_NAME, '/');
    }
  }
}
